import React, { useCallback, useEffect, useState } from 'react';
import { Box, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { appleTokens as t, tint } from '../../../theme/appleTokens';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'back', '0', 'ok'];
const MAX_DIGITS = 8;

/**
 * Pavé numérique pour saisir un code à la souris (ou au doigt) — le clavier physique marche aussi (chiffres,
 * Retour arrière, Entrée). `onSubmit(code)` doit renvoyer une promesse résolue à `true` si le code est bon : sinon le
 * pavé tremble, s'efface et affiche `errorText`. `dark` : sur fond sombre (écran verrouillé).
 */
export default function PinPad({ onSubmit, errorText = 'Code incorrect', dark = false, autoFocusKeyboard = true }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(0);

  const submit = useCallback(async () => {
    if (!code || busy) return;
    setBusy(true);
    setError('');
    try {
      const ok = await onSubmit(code);
      if (!ok) {
        setError(errorText);
        setShake((n) => n + 1);
        setCode('');
      }
    } catch {
      setError('Vérification impossible, réessayez.');
      setCode('');
    } finally {
      setBusy(false);
    }
  }, [busy, code, errorText, onSubmit]);

  const press = useCallback(
    (key) => {
      if (busy) return;
      if (key === 'back') setCode((c) => c.slice(0, -1));
      else if (key === 'ok') void submit();
      else setCode((c) => (c.length >= MAX_DIGITS ? c : c + key));
      if (key !== 'ok') setError('');
    },
    [busy, submit]
  );

  useEffect(() => {
    if (!autoFocusKeyboard) return undefined;
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') press('back');
      else if (e.key === 'Enter') press('ok');
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [autoFocusKeyboard, press]);

  const fg = dark ? '#fff' : t.label;
  const keyBg = dark ? 'rgba(255,255,255,0.10)' : t.surfaceSunken;
  const keyHover = dark ? 'rgba(255,255,255,0.18)' : t.surfaceHover;

  return (
    <Stack alignItems="center" spacing={2.5}>
      <Box
        key={shake}
        sx={{
          display: 'flex',
          gap: 1.25,
          minHeight: 16,
          animation: shake ? 'pinShake 0.35s ease' : 'none',
          '@keyframes pinShake': {
            '0%, 100%': { transform: 'translateX(0)' },
            '20%, 60%': { transform: 'translateX(-8px)' },
            '40%, 80%': { transform: 'translateX(8px)' },
          },
        }}
        aria-live="polite"
        aria-label={`${code.length} chiffre(s) saisi(s)`}
      >
        {Array.from({ length: Math.max(4, code.length) }, (_, i) => (
          <Box
            key={i}
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: error ? t.status.injured.color : fg,
              bgcolor: i < code.length ? (error ? t.status.injured.color : fg) : 'transparent',
              opacity: i < code.length ? 1 : 0.45,
              transition: 'background-color 0.12s ease',
            }}
          />
        ))}
      </Box>
      <Typography sx={{ ...t.type.small, minHeight: 20, color: error ? t.status.injured.color : 'transparent' }}>{error || '·'}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 72px)', gap: 1.5 }}>
        {KEYS.map((key) => {
          const isOk = key === 'ok';
          const label = key === 'back' ? 'Effacer' : isOk ? 'Valider' : key;
          return (
            <ButtonBase
              key={key}
              onClick={() => press(key)}
              disabled={busy || (isOk && !code)}
              aria-label={label}
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: isOk ? t.accent : key === 'back' ? 'transparent' : keyBg,
                color: isOk ? t.accentInk : fg,
                fontSize: 26,
                fontWeight: 500,
                ...t.type.numeric,
                transition: 'background-color 0.12s ease, transform 0.08s ease',
                '&:hover': { bgcolor: isOk ? t.accentStrong || t.accent : keyHover },
                '&:active': { transform: 'scale(0.94)' },
                '&.Mui-disabled': { opacity: isOk ? 0.4 : 0.6 },
                boxShadow: isOk ? `0 0 0 4px ${tint(t.accent, 0.25)}` : 'none',
              }}
            >
              {key === 'back' ? (
                <BackspaceOutlinedIcon sx={{ fontSize: 24 }} />
              ) : isOk ? (
                busy ? <CircularProgress size={24} sx={{ color: t.accentInk }} /> : <ArrowForwardRoundedIcon sx={{ fontSize: 28 }} />
              ) : (
                key
              )}
            </ButtonBase>
          );
        })}
      </Box>
    </Stack>
  );
}
